import { useState } from "react";
import { motion } from "framer-motion";
import { Film, Gamepad2, ChefHat, Music, Heart, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useVirtualDate, RoomType } from "@/hooks/useVirtualDate";
import { toast } from "sonner";

interface VirtualDateInviteProps {
  isOpen: boolean;
  onClose: () => void;
  partnerId: string;
  partnerName: string;
  partnerPhoto?: string | null;
  onRoomCreated: (roomId: string) => void;
}

const DATE_TYPES: { type: RoomType; icon: React.ReactNode; label: string; description: string; gradient: string }[] = [
  {
    type: "game",
    icon: <Gamepad2 className="w-6 h-6" />,
    label: "Play Games",
    description: "Trivia, Would You Rather, Truth or Dare",
    gradient: "from-green-500 to-emerald-500",
  },
  {
    type: "movie",
    icon: <Film className="w-6 h-6" />,
    label: "Watch Together",
    description: "Watch videos in sync",
    gradient: "from-purple-500 to-pink-500",
  },
  {
    type: "cooking",
    icon: <ChefHat className="w-6 h-6" />,
    label: "Cook Together",
    description: "Follow recipes side by side",
    gradient: "from-orange-500 to-red-500",
  },
  {
    type: "music",
    icon: <Music className="w-6 h-6" />,
    label: "Listen Together",
    description: "Share music and playlists",
    gradient: "from-blue-500 to-cyan-500",
  },
];

export const VirtualDateInvite = ({
  isOpen,
  onClose,
  partnerId,
  partnerName,
  partnerPhoto,
  onRoomCreated,
}: VirtualDateInviteProps) => {
  const [selectedType, setSelectedType] = useState<RoomType>("game");
  const [isSending, setIsSending] = useState(false);
  const { createRoom } = useVirtualDate();

  const handleSendInvite = async () => {
    setIsSending(true);
    try {
      const room = await createRoom(partnerId, selectedType);
      if (room) {
        toast.success(`Virtual date invite sent to ${partnerName}!`);
        onRoomCreated(room.id);
        onClose();
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        {/* Header with partner */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-primary/10 to-pink-500/10">
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <Avatar className="w-20 h-20 border-4 border-background shadow-lg">
                    <AvatarImage src={partnerPhoto || undefined} />
                    <AvatarFallback className="text-2xl">{partnerName?.[0]}</AvatarFallback>
                  </Avatar>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-pink-500 text-white"
                  >
                    <Heart className="w-4 h-4" fill="currentColor" />
                  </motion.div>
                </div>
                <div>
                  <h2 className="text-xl font-bold">Virtual Date with {partnerName}</h2>
                  <p className="text-sm text-muted-foreground font-normal mt-1">
                    Choose an activity to do together
                  </p>
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
        </div>

        {/* Date type selector */}
        <div className="p-4 space-y-3">
          {DATE_TYPES.map((dateType) => (
            <motion.button
              key={dateType.type}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => setSelectedType(dateType.type)}
              className={cn(
                "w-full flex items-center gap-4 p-4 rounded-xl transition-all border-2",
                selectedType === dateType.type
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-border/80 bg-card"
              )}
            >
              <div className={cn("p-3 rounded-xl bg-gradient-to-br text-white", dateType.gradient)}>
                {dateType.icon}
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-foreground">{dateType.label}</h3>
                <p className="text-sm text-muted-foreground">{dateType.description}</p>
              </div>
              {selectedType === dateType.type && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-6 h-6 rounded-full bg-primary flex items-center justify-center"
                >
                  <motion.div className="w-2 h-2 rounded-full bg-primary-foreground" />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Actions */}
        <div className="p-4 pt-2 border-t border-border/50 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSendInvite}
            disabled={isSending}
            className="flex-1 gap-2"
          >
            {isSending ? (
              <>Sending...</>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Invite
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
